import type { Dispatch, SetStateAction } from 'react'
import React, { useCallback, useContext as useContextCore, useRef, useState } from 'react'
import type { ApolloError } from '@apollo/client'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import type { ProductMetadata } from '@ztechapps/common.contract'
import { useMutation, useRole } from '@ztechapps/common.hook'
import { AppSettingKey, productPermissions } from '@ztechapps/product.common'
import { queries } from './graphql'
import type { MetadataListItem, MetadataRecord } from './MetadataSchema'
import { hasSelectValue } from './hasSelectValue'
import { useProductMetadata } from './useProductMetadata'
import type { ActiveCell, UseSpreadsheetNavigationResult } from '@ztechapps/webui.data-tools'
import { useSpreadsheetNavigation } from '@ztechapps/webui.data-tools'
import { GridColumn } from './GridColumn'

export type MetadataProviderProps = {
  _id: string
  value?: ProductMetadata[]
  appSettingKey: string
  headerName?: string
  compact?: boolean
  children: React.ReactNode
}

export type MetadataReorderArgs = { oldIndex: number; newIndex: number }

// Names the three editable fields on a `MetadataListItem` row. Used as a discriminator for
// `onTrailingRowChange(field, value)` so call sites stay typed.
export enum MetadataField {
  Key = 'key',
  Value = 'value',
  Unit = 'unit',
}

export type MetadataContext = {
  list: MetadataListItem[]
  setList: Dispatch<SetStateAction<MetadataListItem[]>>
  metadataSchema: MetadataRecord
  setIsChange: Dispatch<SetStateAction<boolean>>
  isChange: boolean
  // `value` is polymorphic — dropdown cells pass option objects, plain text cells pass strings,
  // numeric cells pass numbers. `any` reflects the runtime contract.
  onChange: (value: any, index: number, field: string, otherFields?: Record<any, any>) => void
  onAdd: (value: MetadataListItem) => void
  onDelete: (index: number) => void
  // Trailing-row promotion: typing into the trailing empty row appends a real row to `list` and a
  // fresh trailing row renders below automatically (rowCount = list.length + 1). The trailing row
  // is never part of the saved payload.
  onTrailingRowChange: (field: MetadataField, value: any, otherFields?: Record<any, any>) => void
  onReorder: (event: MetadataReorderArgs) => void
  onSave: (metadataInput?: ProductMetadata[]) => Promise<void>
  onUpdateLoading: boolean
  onUpdateError: ApolloError
  onRevert: () => void
  hasRole: boolean
  headerName?: string
  compact?: boolean
  // Spreadsheet navigation — exposed so every cell can call `getCellProps` without prop drilling.
  activeCell: ActiveCell
  selectCell: UseSpreadsheetNavigationResult['selectCell']
  editingCell: UseSpreadsheetNavigationResult['editingCell']
  enterEditMode: UseSpreadsheetNavigationResult['enterEditMode']
  exitEditMode: UseSpreadsheetNavigationResult['exitEditMode']
  getCellProps: UseSpreadsheetNavigationResult['getCellProps']
  advanceFromCell: UseSpreadsheetNavigationResult['advanceFromCell']
  containerProps: UseSpreadsheetNavigationResult['containerProps']
  // Edit vs view mode. `Layout` shows `MetadataDisplayContent` when true and `MetadataInput` when
  // false. Exposed via context so the maximize modal can flip it back to view mode on Save/Cancel.
  showEdit: boolean
  setShowEdit: (next: boolean) => void
  // Maximize state. `Layout` hides the inline subtree via `d-none` when true; the modal renders a
  // second copy of `MetadataInput`. Provider state (list, isChange, activeCell) is shared across the
  // toggle. Local DOM focus and per-cell react-select menu state are not preserved across mount.
  isMaximized: boolean
  toggleMaximized: () => void
} & Omit<MetadataProviderProps, 'children'>

const context = React.createContext<MetadataContext>({} as MetadataContext)
const CoreProvider = context.Provider

const SPREADSHEET_COLUMN_COUNT = 3

export const MetadataProvider = ({
  children,
  _id,
  value,
  appSettingKey,
  headerName = 'Specifications',
  ...props
}: MetadataProviderProps) => {
  const [onUpdate, , { loading: onUpdateLoading, error: onUpdateError }] = useMutation({ query: queries })

  const [isChange, setIsChange] = useState(false)

  const { metadataSchema } = useProductMetadata({ appSettingKey })
  const [list, setList] = React.useState<MetadataListItem[]>([])
  const [defaultList, setDefaultList] = React.useState<MetadataListItem[]>([])

  React.useEffect(() => {
    if (value && metadataSchema) {
      const mappedData = map(value, (item) => {
        const metadataValue = metadataSchema[item?.key]
        return {
          ...item,
          currentMetadataSchema: metadataValue,
        }
      })
      setList(mappedData)
      setDefaultList(mappedData)
    }
  }, [value, metadataSchema])

  // Batch save sends `list` once. Strips transient fields (`currentMetadataSchema`, `isChange`)
  // before sending — the backend rejects unknown keys. After success, re-snapshot `defaultList`
  // from the post-save list (with each row's `isChange` cleared) so the next Cancel reverts to
  // the LAST-saved state, not the first-loaded state. The Apollo cache write-back will later run
  // the `value` effect against the same data; no race.
  const onSave = async (metadataInput?: ProductMetadata[]) => {
    const source = metadataInput ?? list
    const cleaned = map(source, (item) => {
      const { currentMetadataSchema: _schema, isChange: _isChange, ...rest } = item as MetadataListItem
      return rest as ProductMetadata
    })
    await onUpdate?.({
      input: {
        _id,
        productId: _id,
        settingName: AppSettingKey.PRODUCT_SPECIFICATIONS,
        metadata: cleaned,
      },
    })

    const settled = map(list, (item) => ({ ...item, isChange: false }))
    setList(settled)
    setDefaultList(settled)
    setIsChange(false)
  }

  // All state mutators below use functional `setList(prev => ...)` — closing over `list` creates
  // stale-state hazards under React 18 automatic batching, where two events fired in the same
  // tick read the same closure-captured `list` and overwrite each other.
  const onChange = useCallback(
    (value: any, index: number, field: string, otherFields = {}) => {
      setList((prev) => {
        const next = [...prev]
        next[index] = { ...prev[index], ...otherFields, [field]: value, isChange: true }
        return next
      })
      setIsChange(true)
    },
    [setList, setIsChange]
  )

  const onAdd = (value: MetadataListItem) => {
    if (!value.key) return
    setList((prev) => [...prev, { ...value, isChange: true }])
    setIsChange(true)
  }

  // Row deletion (Backspace on empty row OR trash-icon click). The trailing-row index
  // (`list.length`) is a no-op — there's no committed row to remove (and the trailing row renders
  // no trash icon, so `onDelete` only ever receives a real row index). After delete, focus moves to
  // the previous row's same column.
  //
  // `setIsChange(true)` MUST run unconditionally here, NOT gated by a flag set inside the
  // `setList` updater: React invokes the functional updater during the render phase, so any flag
  // it sets is still false when the synchronous code after `setList` runs — gating on it would
  // skip the dirty mark and leave Save disabled after a delete. The bounds guard stays inside the
  // updater (it needs `prev`); the dirty mark lives outside.
  const onDelete = (index: number) => {
    setList((prev) => {
      if (index < 0 || index >= prev.length) return prev
      const next = [...prev]
      next.splice(index, 1)
      return next
    })
    setIsChange(true)
    setActiveCell((current) => ({ row: Math.max(0, index - 1), col: current.col }))
  }

  // Trailing-row promotion. The trailing row renders at index `list.length` but is NOT part of
  // `list`. Typing into any of its cells appends a real row carrying the typed value (and the
  // schema lookup when the field is `key`). The cell the user just typed in is now at index
  // `list.length - 1`; `activeCell` is pinned there to keep the cursor in place. A fresh empty
  // trailing row renders below automatically because rowCount derives from `list.length + 1`.
  const onTrailingRowChange: MetadataContext['onTrailingRowChange'] = (field, value, otherFields = {}) => {
    const seed: Partial<MetadataListItem> = {}
    if (field === MetadataField.Key) {
      const schema = metadataSchema?.[value]
      seed.key = value
      seed.currentMetadataSchema = schema
    } else if (field === MetadataField.Value) {
      seed.value = value
    } else if (field === MetadataField.Unit) {
      seed.unit = value
    }
    const newRow = { ...seed, ...otherFields, isChange: true } as MetadataListItem
    let promotedRowIndex = 0
    setList((prev) => {
      promotedRowIndex = prev.length
      return [...prev, newRow]
    })
    setIsChange(true)
    setActiveCell((current) => ({ row: promotedRowIndex, col: current.col }))
  }

  // Drag-to-reorder splices `list` and marks dirty. Persists on Save click; no mutation here.
  const onReorder = ({ oldIndex, newIndex }: MetadataReorderArgs) => {
    setList((prev) => {
      const next = [...prev]
      const [moved] = next.splice(oldIndex, 1)
      next.splice(newIndex, 0, moved)
      return next
    })
    setIsChange(true)
  }

  // Cancel reverts everything. `list` snaps back to the last-saved snapshot (`defaultList`);
  // `isChange` clears; the trailing empty row auto-resets (render-derived from `list.length`).
  // Also resets `activeCell` so the cursor lands at a known position.
  const onRevert = () => {
    setList(defaultList)
    setIsChange(false)
    setActiveCell({ row: 0, col: 0 })
  }
  const { hasRole } = useRole({ roleName: productPermissions.canEdit.value })

  // Backspace-deletion predicates. The cell is empty (its specific field has no content) AND every
  // field on the row is empty. `lodash/isEmpty` returns true for numbers (no length/keys), so the
  // numeric `value` column coerces to string before checking. The trailing row at index
  // `list.length` has no item — `!item` short-circuits to "empty" and Backspace falls through to
  // `onDelete(list.length)`, which is guarded as a no-op.
  const fieldByColumn = (col: number): keyof MetadataListItem | undefined => {
    if (col === GridColumn.Name) return 'key'
    if (col === GridColumn.Value) return 'value'
    if (col === GridColumn.Unit) return 'unit'
    return undefined
  }

  const isCellEmpty = (row: number, col: number): boolean => {
    const item = list[row]
    if (!item) return true
    const field = fieldByColumn(col)
    if (!field) return true
    const raw = item[field]
    return isEmpty(typeof raw === 'number' ? String(raw) : raw)
  }

  const isRowEmpty = (row: number): boolean => {
    const item = list[row]
    if (!item) return true
    const valueAsString = typeof item.value === 'number' ? String(item.value) : item.value
    return isEmpty(item.key) && isEmpty(valueAsString) && isEmpty(item.unit)
  }

  // Snapshot the cell's value at edit-enter so Esc can revert. Cleared on commit (any non-Esc
  // exit) and on revert (the actual restore).
  const editSnapshotRef = useRef<{ row: number; col: number; value: any } | null>(null)
  const onEnterEditCallback = useCallback(
    (row: number, col: number, initialChar: string | null) => {
      const item = list[row]
      const field = fieldByColumn(col)
      if (!field) return
      editSnapshotRef.current = item ? { row, col, value: item[field] } : { row, col, value: undefined }
      // Sheets behavior: typing a letter in view mode REPLACES a text or number Value cell with
      // that letter. A select-type Value, like the Key and Unit dropdowns, only filters its menu
      // with the seed (via `defaultInputValue` in the cell renderer) — the value changes only when
      // an option is committed.
      if (initialChar !== null && col === GridColumn.Value && !hasSelectValue(item)) {
        onChange(initialChar, row, MetadataField.Value)
      }
    },
    [list, onChange]
  )
  const onRevertEditCallback = useCallback(
    (row: number, col: number) => {
      const snap = editSnapshotRef.current
      if (!snap || snap.row !== row || snap.col !== col) return
      const field = fieldByColumn(col)
      if (!field) return
      // Revert is a row mutation on a single field — funnel through `onChange` so dirty tracking,
      // schema lookups, and the trailing-row promotion path stay consistent.
      onChange(snap.value, row, field)
      editSnapshotRef.current = null
    },
    [onChange]
  )

  const {
    activeCell,
    setActiveCell,
    selectCell,
    editingCell,
    enterEditMode,
    exitEditMode,
    getCellProps,
    advanceFromCell,
    containerProps,
  } = useSpreadsheetNavigation({
    // The trailing empty row is a navigable cell at index `list.length`. Bumping `rowCount` lets
    // ArrowDown / Tab / Enter from the last committed row reach the trailing row's cells.
    rowCount: list.length + 1,
    columnCount: SPREADSHEET_COLUMN_COUNT,
    onDeleteEmptyRow: onDelete,
    isCellEmpty,
    isRowEmpty,
    onEnterEdit: onEnterEditCallback,
    onRevertEdit: onRevertEditCallback,
  })

  // Edit vs view mode. Default: view (true). Clicking Edit flips to false AND opens the modal.
  const [showEdit, setShowEditState] = useState(true)
  const setShowEdit = useCallback((next: boolean) => setShowEditState(next), [])

  // Maximize state. The toggle is the single entry point; the maximize button, the Esc keypress,
  // and the modal's Save/Cancel all flip this flag.
  const [isMaximized, setIsMaximized] = useState(false)
  const toggleMaximized = useCallback(() => setIsMaximized((prev) => !prev), [])

  return (
    <CoreProvider
      value={{
        ...props,
        _id,
        list,
        setList,
        metadataSchema,
        setIsChange,
        isChange,
        onChange,
        onAdd,
        onDelete,
        onTrailingRowChange,
        onReorder,
        onSave,
        onUpdateLoading,
        onUpdateError,
        onRevert,
        hasRole,
        headerName,
        value,
        appSettingKey,
        activeCell,
        selectCell,
        editingCell,
        enterEditMode,
        exitEditMode,
        getCellProps,
        advanceFromCell,
        containerProps,
        showEdit,
        setShowEdit,
        isMaximized,
        toggleMaximized,
      }}
    >
      {children}
    </CoreProvider>
  )
}

export const useMetadataContext = () => useContextCore(context)
